import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Stack,
    IconButton,
    Tooltip,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem
} from '@mui/material';
import { ArrowLeft, Mail, Phone, Download, Eye, Clock, Edit3 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getJobApplications, updateJobApplication, getJobApplicationsByJob } from '../../../Services/ContentHub/JobsApi/JobsApi';
import ApplicationDetailsModal from './ApplicationDetailsModal';
import { useGlobalSnackbar } from '../../../Components/Common/GlobalSanckbar';

const JobEnquires = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const location = useLocation();
    const { jobId, jobTitle } = location.state || {};
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Update Status Modal State
    const [openStatusModal, setOpenStatusModal] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [statusForm, setStatusForm] = useState({
        status: '',
        additionalNotes: ''
    });

    // Details Modal State
    const [openDetailsModal, setOpenDetailsModal] = useState(false);
    const [viewingApp, setViewingApp] = useState(null);

    const fetchApplications = useCallback(async (signal) => {
        try {
            setLoading(true);
            setError(null);
            let response;
            if (jobId) {
                response = await getJobApplicationsByJob(jobId);
            } else {
                response = await getJobApplications(signal);
            }

            if (response && response.success) {
                setApplications(response.data || []);
            } else {
                setError("Failed to load job applications.");
            }
        } catch (err) {
            if (err.name !== 'CanceledError' && !err.message?.includes('canceled')) {
                setError("Failed to fetch job applications.");
            }
        } finally {
            setLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        const controller = new AbortController();
        fetchApplications(controller.signal);
        return () => controller.abort();
    }, [fetchApplications]);

    const handleOpenStatusModal = (app) => {
        setSelectedApp(app);
        setStatusForm({
            status: app.status || 'pending',
            additionalNotes: app.additionalNotes || ''
        });
        setOpenStatusModal(true);
    };

    const handleCloseStatusModal = () => {
        setOpenStatusModal(false);
        setSelectedApp(null);
    };

    const handleViewDetails = (app) => {
        setViewingApp(app);
        setOpenDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setOpenDetailsModal(false);
        setViewingApp(null);
    };

    const handleUpdateStatus = async () => {
        if (!selectedApp) return;

        setSubmitting(true);
        try {
            const response = await updateJobApplication(selectedApp.id, statusForm);
            if (response && response.success) {
                showSnackbar("Application status updated successfully!", "success");
                handleCloseStatusModal();
                fetchApplications();
            } else {
                showSnackbar(response?.message || "Failed to update status", "error");
            }
        } catch (err) {
            showSnackbar("An error occurred while updating status", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            // case 'pending': return { bg: '#FFF3E0', color: '#EF6C00' };
            case 'reviewing': return { bg: '#E3F2FD', color: '#1976D2' };
            case 'shortlisted': return { bg: '#E8F5E9', color: '#2E7D32' };
            case 'rejected': return { bg: '#FFEBEE', color: '#C62828' };
            default: return { bg: '#F5F5F5', color: '#666' };
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                <IconButton disableFocusRipple disableRipple disableTouchRipple onClick={() => navigate(-1)} sx={{ bgcolor: '#fff', border: '1px solid #E8E8E8', borderRadius: '6px', p: 1 }}>
                    <ArrowLeft size={16} />
                </IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        {jobTitle ? `Applications - ${jobTitle}` : 'Job Applications'}
                    </Typography>
                </Box>
            </Box>


            <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                <TableContainer>
                    <Table sx={{ minWidth: 1250 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Applicant</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Job Role</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Contact</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                        <Typography color="error" sx={{ fontSize: '0.85rem' }}>{error}</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : applications.length > 0 ? (
                                applications.map((app, idx) => (
                                    <TableRow key={app.id} sx={{
                                        transition: 'background-color 0.2s',
                                        '& td': { borderBottom: '1px solid #F0F0F0' },
                                        '&:last-child td': { borderBottom: 0 }
                                    }}>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                <Avatar sx={{ bgcolor: 'rgba(255, 189, 101, 0.15)', color: '#FFBD65', fontWeight: 600, fontSize: '0.8rem', width: 32, height: 32 }}>
                                                    {app.name?.charAt(0).toUpperCase() || app.fullName?.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography sx={{ fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>{app.name || app.fullName}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#444', fontSize: '0.8rem' }}>{app.job_title || app.Job?.title || 'General Application'}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                            <Stack spacing={0.5}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#666' }}>
                                                    <Mail size={12} />
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{app.email}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#666' }}>
                                                    <Phone size={12} />
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{app.mobile || app.mobileNumber || '-'}</Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                            <Box
                                                onClick={() => handleOpenStatusModal(app)}
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 1.5, py: 0.3,
                                                    borderRadius: '4px',
                                                    bgcolor: getStatusColor(app.status).bg,
                                                    color: getStatusColor(app.status).color,
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    cursor: 'pointer',
                                                    '&:hover': { opacity: 0.8 }
                                                }}
                                            >
                                                {app.status || 'pending'}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {app.cuName || app.name || app.fullName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {app.uuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(app.createdAt)}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(app.updatedAt)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        disableFocusRipple
                                                        disableRipple
                                                        disableTouchRipple
                                                        size="small"
                                                        onClick={() => handleViewDetails(app)}
                                                        sx={{ color: '#FFBD65' }}
                                                    >
                                                        <Eye size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Update Status">
                                                    <IconButton disableFocusRipple disableRipple disableTouchRipple onClick={() => handleOpenStatusModal(app)} size="small" sx={{ color: '#FFBD65' }}>
                                                        <Edit3 size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Download CV">
                                                    <IconButton
                                                        disableFocusRipple
                                                        disableRipple
                                                        disableTouchRipple
                                                        size="small"
                                                        sx={{ color: '#FFBD65' }}
                                                        onClick={() => app.cvFile && window.open(app.cvFile, '_blank')}
                                                    >
                                                        <Download size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                        No job applications found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Status Update Modal */}
            <Dialog open={openStatusModal} onClose={handleCloseStatusModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>Update Application Status</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Status"
                            value={statusForm.status}
                            onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }
                            }}
                        >
                            {/* <MenuItem value="pending">Pending</MenuItem> */}
                            <MenuItem value="reviewing" sx={{ fontSize: '0.85rem' }}>Reviewing</MenuItem>
                            <MenuItem value="shortlisted" sx={{ fontSize: '0.85rem' }}>Shortlisted</MenuItem>
                            <MenuItem value="rejected" sx={{ fontSize: '0.85rem' }}>Rejected</MenuItem>
                        </TextField>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                            label="Additional Notes"
                            placeholder="Add any internal notes about this applicant..."
                            value={statusForm.additionalNotes}
                            onChange={(e) => setStatusForm({ ...statusForm, additionalNotes: e.target.value })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 1 }}>
                    <Button disableElevation disableFocusRipple disableRipple disableTouchRipple onClick={handleCloseStatusModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>Cancel</Button>
                    <Button
                        disableElevation disableFocusRipple disableRipple disableTouchRipple
                        onClick={handleUpdateStatus}
                        disabled={submitting}
                        variant="contained"
                        sx={{
                            bgcolor: '#FFBD65',
                            color: '#fff',
                            textTransform: 'none',
                            borderRadius: '6px',
                            px: 3,
                            fontSize: '0.85rem'
                        }}
                    >
                        {submitting ? <CircularProgress size={20} color="inherit" /> : 'Update Status'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Details Modal */}
            <ApplicationDetailsModal
                open={openDetailsModal}
                onClose={handleCloseDetailsModal}
                application={viewingApp}
            />
        </Box>
    );
};

export default JobEnquires;

